import {action, makeObservable, observable} from 'mobx'

/**
 * Extended Map which calls factory function to supply missing values.
 * Only modifies Map.get to always return a value. Other functionality is not modified.
 */
export class DefaultMap<TKey, TValue> implements Map<TKey, TValue> {
    public [Symbol.toStringTag] = 'DefaultMap'

    @observable private map: Map<TKey, TValue>

    constructor(private valueFactory: (key: TKey) => TValue, init?: [TKey, TValue][]) {
        this.map = new Map(init)

        makeObservable(this)
        this.get = this.get.bind(this)
        this.has = this.has.bind(this)
        this.keys = this.keys.bind(this)
        this.values = this.values.bind(this)
        this.entries = this.entries.bind(this)
    }

    public get(key: TKey): TValue {
        let value = this.map.get(key)
        if (!value) {
            value = this.valueFactory(key)
            this.set(key, value)

            // needed because MobX modifies referencial types during "this.map.set", so "value" and "this.map.get(key)" are not equal
            value = this.map.get(key) as TValue
        }

        return value
    }

    // Implementation of Map<TKey, TValue> delegated to this.map

    @action.bound
    public clear(): void {
        this.map.clear()
    }

    @action.bound
    public delete(key: TKey): boolean {
        return this.map.delete(key)
    }

    @action.bound
    public forEach(callbackfn: (value: TValue, key: TKey, map: Map<TKey, TValue>) => void, thisArg?: any): void {
        this.map.forEach(callbackfn, thisArg)
    }

    public has(key: TKey): boolean {
        return this.map.has(key)
    }

    @action.bound
    public set(key: TKey, value: TValue): this {
        this.map.set(key, value)
        return this
    }

    public get size() {
        return this.map.size
    }

    public keys(): IterableIterator<TKey> {
        return this.map.keys()
    }

    public values(): IterableIterator<TValue> {
        return this.map.values()
    }

    public entries(): IterableIterator<[TKey, TValue]> {
        return this.map.entries()
    }

    public [Symbol.iterator](): IterableIterator<[TKey, TValue]> {
        return this.map[Symbol.iterator]()
    }
}

export function mapReplaceAll<K, V>(map: Map<K, V>, newItems: Iterable<[K, V]>): void {
    map.clear()
    for (const [k, v] of newItems) {
        map.set(k, v)
    }
}

export function mapGetOrInsert<K, V>(map: Map<K, V>, key: K, orInserted: V): V {
    if (!map.has(key)) {
        map.set(key, orInserted)
    }

    return map.get(key) as V
}

export function mapPushToItem<K, VEl>(map: Map<K, VEl[]>, key: K, el: VEl): void {
    if (!map.has(key)) {
        map.set(key, [])
    }

    const bucket = map.get(key) as VEl[]
    bucket.push(el)
}

export function mapAddToItem<K, VEl>(map: Map<K, Set<VEl>>, key: K, el: VEl): void {
    if (!map.has(key)) {
        map.set(key, new Set())
    }

    const bucket = map.get(key) as Set<VEl>
    bucket.add(el)
}

export function mapDeleteItem<K, VEl>(map: Map<K, Set<VEl>>, key: K, el: VEl): void {
    const set = map.get(key)
    if (!set) {
        return
    }

    set.delete(el)
}

export function mapDeleteWhere<K, V>(map: Map<K, V>, predicate: (v: V, k: K) => boolean): void {
    const keysToDelete: K[] = []
    for (const [key, value] of map.entries()) {
        if (predicate(value, key)) {
            keysToDelete.push(key)
        }
    }

    keysToDelete.forEach(k => map.delete(k))
}

/**
 * Moves value stored in "from" key to "to" key.
 * Does nothing if the "map" doesn't have key "from" or if "from" equals "to"
 */
export function mapMoveKey<TKey, TValue>(map: Map<TKey, TValue>, from: TKey, to: TKey) {
    if (map.has(from) && from !== to) {
        map.set(to, map.get(from) as TValue)
        map.delete(from)
    }
}
